import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiX } from 'react-icons/fi';
import ExcelTable from '../../../component/library/excelTable';
import { getAuthLocalStorage } from '../../../storage/localStorage';
import { formatDate } from '../../../helper/utils';
import { SearchLogService } from '../../../service/admin/searchLogService';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import './style.css';

const UserDetails = () => {
  const message = useMessage();
  const [searchParams] = useSearchParams();
  const dataKey = searchParams.get('key');
  const detailType = searchParams.get('type');
  const imageIdFromQuery = searchParams.get('imageId');
  const userIdFromQuery = searchParams.get('userId');
  const userEmailFromQuery = searchParams.get('userEmail');
  const identifierFromQuery = searchParams.get('identifier');
  const rangeFromQuery = searchParams.get('range') || '24h';
  const startDateFromQuery = searchParams.get('startDate');
  const endDateFromQuery = searchParams.get('endDate');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredKeywordId, setHoveredKeywordId] = useState(null);
  const [hoverCardPosition, setHoverCardPosition] = useState({ top: 0, left: 0 });
  const hoverRefs = useRef({});
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [assigningKeywords, setAssigningKeywords] = useState(false);
  const [imageProductsCurrentPage, setImageProductsCurrentPage] = useState(() => {
    const page = Number(searchParams.get('page'));
    return Number.isFinite(page) && page > 0 ? page : 1;
  });
  const [imageProductsRowsPerPage, setImageProductsRowsPerPage] = useState(() => {
    const limit = Number(searchParams.get('limit'));
    return Number.isFinite(limit) && limit > 0 ? limit : 10;
  });
  const [detailsCurrentPage, setDetailsCurrentPage] = useState(() => {
    const page = Number(searchParams.get('page'));
    return Number.isFinite(page) && page > 0 ? page : 1;
  });
  const [detailsRowsPerPage, setDetailsRowsPerPage] = useState(() => {
    const limit = Number(searchParams.get('limit'));
    return Number.isFinite(limit) && limit > 0 ? limit : 10;
  });
  
  const authData = getAuthLocalStorage();
  const currentUserId = authData?.loginHeader || null;

  const updateDetailQueryPageLimit = (page, limit) => {
    const nextQuery = new URLSearchParams(window.location.search);
    nextQuery.set('page', String(page));
    nextQuery.set('limit', String(limit));
    window.history.replaceState({}, '', `${window.location.pathname}?${nextQuery.toString()}`);
  };

  const buildClientPagination = (totalCount) => {
    const safeTotal = totalCount || 0;
    const totalPages = safeTotal > 0 ? Math.ceil(safeTotal / detailsRowsPerPage) : 1;
    const safePage = Math.min(Math.max(detailsCurrentPage, 1), totalPages);
    return {
      totalCount: safeTotal,
      totalPages,
      currentPage: safePage,
      hasPrevPage: safePage > 1,
      hasNextPage: safePage < totalPages,
      startIndex: safeTotal === 0 ? 0 : ((safePage - 1) * detailsRowsPerPage) + 1,
      endIndex: Math.min(safePage * detailsRowsPerPage, safeTotal),
    };
  };

  const formatCartItems = (cartItems = []) => {
    const formattedCartItems = [];

    cartItems.forEach(item => {
      const product = item.product || {};
      const variants = item.variants || [];

      if (variants.length > 0) {
        variants.forEach(variant => {
          const variantImage = variant.skuAttributes?.find(attr => attr.skuImageUrl)?.skuImageUrl
            || variant.skuAttributes?.find(attr => attr.thumbnailImage)?.thumbnailImage
            || product.imgUrl || product.image || (product.images && product.images[0]) || '';

          const variantNameParts = (variant.skuAttributes || [])
            .filter(attr => attr.valueTrans)
            .map(attr => attr.valueTrans)
            .join(', ');
          const variantName = variantNameParts || 'N/A';

          const pricingMode = item.pricingMode || 'dropshipping';
          const variantPrice = pricingMode === 'wholesale'
            ? (variant.wholesaleFinalPrice || 0)
            : (variant.finalPrice || 0);

          formattedCartItems.push({
            variantImage,
            variantName,
            variantPrice,
            pricingMode,
            quantity: variant.quantity || 0,
            productSku: product.sku || product.productSku || 'N/A',
            productTitle: product.title || product.subject || product.name || 'N/A',
            imgUrl: variantImage,
            sku: product.sku || product.productSku || 'N/A',
            title: product.title || product.subject || product.name || 'N/A',
            sp: product.sp || product.price || product.vendor_price || 0,
            variantSkuId: variant.skuId || 'N/A',
            variantSpecId: variant.specId || 'N/A',
            variantFinalPrice: variantPrice,
            variantAttributes: variant.skuAttributes || [],
            variantWeight: variant.weight || 0,
            variantDimensions: variant.length && variant.width && variant.height
              ? `${variant.length}x${variant.width}x${variant.height}`
              : 'N/A',
            breakdown: item.breakdown || null,
          });
        });
      } else {
        formattedCartItems.push({
          variantImage: product.imgUrl || product.image || (product.images && product.images[0]) || '',
          variantName: 'N/A',
          variantPrice: 0,
          pricingMode: item.pricingMode || 'N/A',
          quantity: item.quantity || 0,
          productSku: product.sku || product.productSku || 'N/A',
          productTitle: product.title || product.subject || product.name || 'N/A',
          imgUrl: product.imgUrl || product.image || (product.images && product.images[0]) || '',
          sku: product.sku || product.productSku || 'N/A',
          title: product.title || product.subject || product.name || 'N/A',
          sp: product.sp || product.price || product.vendor_price || 0,
          variantSkuId: 'N/A',
          variantSpecId: 'N/A',
          variantFinalPrice: 0,
          variantAttributes: [],
          variantWeight: 0,
          variantDimensions: 'N/A',
          breakdown: item.breakdown || null,
        });
      }
    });

    return formattedCartItems;
  };

  const fetchProductIntentDetailsInTab = async () => {
    try {
      setLoading(true);
      const response = await SearchLogService.getUserProductIntentDetails({
        userId: userIdFromQuery,
        eventType: detailType,
        range: rangeFromQuery,
        startDate: startDateFromQuery || undefined,
        endDate: endDateFromQuery || undefined,
      });

      if (response?.result?.code === 200 || response?.status?.code === 200) {
        const responseData = response?.result?.data || response?.data || {};
        setData({
          type: detailType,
          userEmail: userEmailFromQuery || 'User',
          title: detailType === 'pdp_open' ? 'PDP Opens' : 'Add to Cart',
          products: responseData.products || [],
        });
      } else {
        setData({ error: 'Error loading product details' });
      }
    } catch (error) {
      console.error('Error loading product details:', error);
      setData({ error: 'Error loading product details' });
      message.showToast('Error loading product details', ToastTypes.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const fetchCartInTab = async () => {
    try {
      setLoading(true);
      const response = await SearchLogService.getUserCart(userIdFromQuery);
      if (response?.result?.code === 200 || response?.status?.code === 200) {
        const user = response?.result?.data || response?.data || {};
        setData({
          type: 'cart',
          userEmail: userEmailFromQuery || 'User',
          title: 'Current Add to Cart',
          cartItems: formatCartItems(user.addToCart || []),
        });
      } else {
        setData({ error: 'Error loading cart details' });
      }
    } catch (error) {
      console.error('Error loading cart details:', error);
      setData({ error: 'Error loading cart details' });
      message.showToast('Error loading cart details', ToastTypes.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const fetchSearchKeywordsInTab = async () => {
    try {
      setLoading(true);
      const response = await SearchLogService.getUserSearchKeywords(identifierFromQuery);
      if (response?.result?.code === 200 || response?.status?.code === 200) {
        const responseData = response?.result?.data || response?.data || {};
        const formattedKeywords = (responseData.keywords || []).map(kw => ({
          keyword: kw.keyword || 'N/A',
          searchCount: kw.searchCount || 0,
          lastSearched: kw.lastSearched || kw.createdAt || null,
          ip: kw.ip || 'N/A',
          searchTimestamps: kw.searchTimestamps || [],
        }));

        setData({
          type: 'searchKeywords',
          userEmail: userEmailFromQuery || identifierFromQuery || 'User',
          title: 'Search Keywords',
          keywords: formattedKeywords,
        });
      } else {
        setData({ error: 'Error loading search keywords' });
      }
    } catch (error) {
      console.error('Error loading search keywords:', error);
      setData({ error: 'Error loading search keywords' });
      message.showToast('Error loading search keywords', ToastTypes.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const fetchImageProductsByPage = async (imageId, page = 1, limit = imageProductsRowsPerPage) => {
    try {
      setLoading(true);
      setSelectedKeywords([]);
      const response = await SearchLogService.getProductsByImageId(imageId, page, limit);
      if (response?.result?.code === 200 || response?.status?.code === 200) {
        const responseData = response?.result?.data || response?.data || {};
        setData({
          type: 'image-search',
          title: 'Image Search Products',
          imageId,
          products: responseData.products || [],
          pagination: responseData.pagination || null,
        });
        setImageProductsCurrentPage(responseData?.pagination?.currentPage || page);
      } else {
        setData({ error: 'Failed to load products' });
      }
    } catch (error) {
      console.error('Error loading image products:', error);
      setData({ error: 'Error loading products' });
      message.showToast('Error loading products', ToastTypes.ERROR);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (detailType === 'image-search' && imageIdFromQuery) {
      fetchImageProductsByPage(imageIdFromQuery, imageProductsCurrentPage, imageProductsRowsPerPage);
      return;
    }

    if ((detailType === 'pdp_open' || detailType === 'add_to_cart') && userIdFromQuery) {
      fetchProductIntentDetailsInTab();
      return;
    }

    if (detailType === 'cart' && userIdFromQuery) {
      fetchCartInTab();
      return;
    }

    if (detailType === 'searchKeywords' && identifierFromQuery) {
      fetchSearchKeywordsInTab();
      return;
    }

    if (dataKey) {
      try {
        const storedData = sessionStorage.getItem(dataKey);
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          setData(parsedData);
          // Clean up after reading
          sessionStorage.removeItem(dataKey);
        } else {
          setData({ error: 'Data not found or expired' });
        }
      } catch (error) {
        console.error('Error reading data:', error);
        setData({ error: 'Error loading data' });
      } finally {
        setLoading(false);
      }
    } else {
      setData({ error: 'No data key provided' });
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey, detailType, imageIdFromQuery, userIdFromQuery, identifierFromQuery]);

  if (loading) {
    return (
      <div className="user-details-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!data || data.error) {
    return (
      <div className="user-details-page">
        <div className="error-container">
          <p>{data?.error || 'Data not found'}</p>
          <button onClick={() => window.close()} className="retry-btn">
            Close
          </button>
        </div>
      </div>
    );
  }

  const renderTable = () => {
    if (data.type === 'cart') {
      const allCartItems = data.cartItems || [];
      const cartPagination = buildClientPagination(allCartItems.length);
      const paginatedCartItems = allCartItems.slice(
        (cartPagination.currentPage - 1) * detailsRowsPerPage,
        cartPagination.currentPage * detailsRowsPerPage
      );

      return (
        <>
          <ExcelTable
            title={`Cart Products (Total: ${allCartItems.length})`}
            data={paginatedCartItems}
            columns={[
              {
                key: 'variantImage',
                label: 'Variant Image',
                width: 100,
                render: (value) => value ? (
                  <img src={value} alt="Variant" style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }} />
                ) : (
                  <div style={{ width: '60px', height: '60px', backgroundColor: '#f3f4f6', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#9ca3af' }}>No Image</div>
                ),
              },
              {
                key: 'variantSkuId',
                label: 'Variant SKU ID',
                width: 150,
                render: (value) => value !== 'N/A' ? value : 'N/A',
              },
              {
                key: 'variantName',
                label: 'Variant Name',
                width: 200,
                wrap: true,
                render: (value) => value || 'N/A',
              },
              {
                key: 'variantPrice',
                label: 'Variant Price',
                width: 150,
                render: (value, row) => {
                  const price = value || 0;
                  const pricingMode = row.pricingMode || 'dropshipping';
                  const typeLabel = pricingMode === 'wholesale' ? 'WS' : 'DS';
                  const typeColor = pricingMode === 'wholesale' ? '#92400e' : '#1e40af';
                  const typeBg = pricingMode === 'wholesale' ? '#fef3c7' : '#dbeafe';

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span style={{ fontWeight: '600' }}>
                        {price > 0 ? `₹${parseFloat(price).toFixed(2)}` : 'N/A'}
                      </span>
                      <span style={{
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: '600',
                        backgroundColor: typeBg,
                        color: typeColor,
                        width: 'fit-content',
                      }}>
                        {typeLabel}
                      </span>
                    </div>
                  );
                },
              },
              {
                key: 'sp',
                label: 'Product SP',
                width: 100,
                render: (value) => value ? `₹${parseFloat(value).toFixed(2)}` : 'N/A',
              },
              {
                key: 'quantity',
                label: 'Quantity',
                width: 100,
                render: (value) => value || 0,
              },
              {
                key: 'productSku',
                label: 'Product SKU',
                width: 150,
                render: (value) => value || 'N/A',
              },
              {
                key: 'productTitle',
                label: 'Product Title',
                width: 300,
                wrap: true,
                render: (value) => value || 'N/A',
              },
            ]}
            userId={currentUserId}
            showToolbar={true}
            sortable={true}
            resizable={true}
            toolbarRightActions={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '13px', color: '#64748b' }}>Rows:</label>
                <select
                  value={detailsRowsPerPage}
                  onChange={(e) => {
                    const nextLimit = Number(e.target.value);
                    setDetailsRowsPerPage(nextLimit);
                    setDetailsCurrentPage(1);
                    updateDetailQueryPageLimit(1, nextLimit);
                  }}
                  style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', backgroundColor: '#fff', fontSize: '13px', cursor: 'pointer' }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            }
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '6px', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '13px', color: '#64748b' }}>
              Showing {cartPagination.startIndex} to {cartPagination.endIndex} of {cartPagination.totalCount}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => {
                  const prevPage = cartPagination.currentPage - 1;
                  setDetailsCurrentPage(prevPage);
                  updateDetailQueryPageLimit(prevPage, detailsRowsPerPage);
                }}
                disabled={!cartPagination.hasPrevPage || loading}
                style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', backgroundColor: (!cartPagination.hasPrevPage || loading) ? '#f1f5f9' : '#fff', color: (!cartPagination.hasPrevPage || loading) ? '#94a3b8' : '#475569', cursor: (!cartPagination.hasPrevPage || loading) ? 'not-allowed' : 'pointer', fontSize: '13px' }}
              >
                Previous
              </button>
              <span style={{ fontSize: '13px', color: '#64748b' }}>
                Page {cartPagination.currentPage} of {cartPagination.totalPages}
              </span>
              <button
                onClick={() => {
                  const nextPage = cartPagination.currentPage + 1;
                  setDetailsCurrentPage(nextPage);
                  updateDetailQueryPageLimit(nextPage, detailsRowsPerPage);
                }}
                disabled={!cartPagination.hasNextPage || loading}
                style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', backgroundColor: (!cartPagination.hasNextPage || loading) ? '#f1f5f9' : '#fff', color: (!cartPagination.hasNextPage || loading) ? '#94a3b8' : '#475569', cursor: (!cartPagination.hasNextPage || loading) ? 'not-allowed' : 'pointer', fontSize: '13px' }}
              >
                Next
              </button>
            </div>
          </div>
        </>
      );
    } else if (data.type === 'searchKeywords') {
      const allKeywords = data.keywords || [];
      const keywordsPagination = buildClientPagination(allKeywords.length);
      const paginatedKeywords = allKeywords.slice(
        (keywordsPagination.currentPage - 1) * detailsRowsPerPage,
        keywordsPagination.currentPage * detailsRowsPerPage
      );

      return (
        <>
          <ExcelTable
            title={`Search Keywords (Total: ${allKeywords.length})`}
            data={paginatedKeywords}
            columns={[
              {
                key: 'keyword',
                label: 'Keyword',
                width: 300,
                render: (value) => <strong>{value}</strong>,
              },
              {
                key: 'searchCount',
                label: 'Search Count',
                width: 150,
                render: (value, row) => {
                  const keyword = row.keyword;
                  const keywordId = `${keyword}-${row.ip || 'unknown'}`;
                  return (
                    <div
                      ref={(el) => {
                        if (el) hoverRefs.current[keywordId] = el;
                      }}
                      onMouseEnter={(e) => {
                        const element = e.currentTarget;
                        const rect = element.getBoundingClientRect();
                        setHoverCardPosition({ top: rect.top, left: rect.left });
                        setHoveredKeywordId(keywordId);
                      }}
                      onMouseLeave={() => setHoveredKeywordId(null)}
                      style={{ position: 'relative', display: 'inline-block', cursor: 'pointer', zIndex: 1 }}
                    >
                      <span style={{ color: '#3b82f6', textDecoration: 'underline' }}>{value || 0}</span>
                    </div>
                  );
                },
              },
              {
                key: 'ip',
                label: 'IP Address',
                width: 150,
                render: (value) => <code>{value}</code>,
              },
              {
                key: 'lastSearched',
                label: 'Last Searched',
                width: 180,
                render: (value) => value ? formatDate(new Date(value)) : 'N/A',
              },
            ]}
            userId={currentUserId}
            showToolbar={true}
            sortable={true}
            resizable={true}
            toolbarRightActions={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '13px', color: '#64748b' }}>Rows:</label>
                <select
                  value={detailsRowsPerPage}
                  onChange={(e) => {
                    const nextLimit = Number(e.target.value);
                    setDetailsRowsPerPage(nextLimit);
                    setDetailsCurrentPage(1);
                    updateDetailQueryPageLimit(1, nextLimit);
                  }}
                  style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', backgroundColor: '#fff', fontSize: '13px', cursor: 'pointer' }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            }
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '6px', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '13px', color: '#64748b' }}>
              Showing {keywordsPagination.startIndex} to {keywordsPagination.endIndex} of {keywordsPagination.totalCount}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => {
                  const prevPage = keywordsPagination.currentPage - 1;
                  setDetailsCurrentPage(prevPage);
                  updateDetailQueryPageLimit(prevPage, detailsRowsPerPage);
                }}
                disabled={!keywordsPagination.hasPrevPage || loading}
                style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', backgroundColor: (!keywordsPagination.hasPrevPage || loading) ? '#f1f5f9' : '#fff', color: (!keywordsPagination.hasPrevPage || loading) ? '#94a3b8' : '#475569', cursor: (!keywordsPagination.hasPrevPage || loading) ? 'not-allowed' : 'pointer', fontSize: '13px' }}
              >
                Previous
              </button>
              <span style={{ fontSize: '13px', color: '#64748b' }}>
                Page {keywordsPagination.currentPage} of {keywordsPagination.totalPages}
              </span>
              <button
                onClick={() => {
                  const nextPage = keywordsPagination.currentPage + 1;
                  setDetailsCurrentPage(nextPage);
                  updateDetailQueryPageLimit(nextPage, detailsRowsPerPage);
                }}
                disabled={!keywordsPagination.hasNextPage || loading}
                style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', backgroundColor: (!keywordsPagination.hasNextPage || loading) ? '#f1f5f9' : '#fff', color: (!keywordsPagination.hasNextPage || loading) ? '#94a3b8' : '#475569', cursor: (!keywordsPagination.hasNextPage || loading) ? 'not-allowed' : 'pointer', fontSize: '13px' }}
              >
                Next
              </button>
            </div>
          </div>
        </>
      );
    } else if (data.type === 'pdp_open' || data.type === 'add_to_cart') {
      const allProducts = data.products || [];
      const productPagination = buildClientPagination(allProducts.length);
      const paginatedProducts = allProducts.slice(
        (productPagination.currentPage - 1) * detailsRowsPerPage,
        productPagination.currentPage * detailsRowsPerPage
      );

      return (
        <>
          <ExcelTable
            title={`Products ${data.type === 'pdp_open' ? 'Opened' : 'Added to Cart'} (Total: ${allProducts.length})`}
            data={paginatedProducts}
            columns={[
              {
                key: 'imgUrl',
                label: 'Image',
                width: 100,
                render: (value, row) => {
                  const imageUrl = row.thumbnailImage || value;
                  return imageUrl ? (
                    <img src={imageUrl} alt="Product" style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }} />
                  ) : (
                    <div style={{ width: '60px', height: '60px', backgroundColor: '#f3f4f6', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#9ca3af' }}>No Image</div>
                  );
                },
              },
              {
                key: 'sku',
                label: 'SKU',
                width: 150,
              },
              {
                key: 'title',
                label: 'Product Title',
                width: 300,
                wrap: true,
              },
              {
                key: 'sp',
                label: 'SP',
                width: 100,
                render: (value, row) => {
                  const price = value || row.actualPrice || row.consignPrice || row.fenxiaoPriceInfo?.offerPrice || null;
                  if (price !== null && price !== undefined && !isNaN(price) && price > 0) {
                    return `₹${parseFloat(price).toFixed(2)}`;
                  }
                  return 'N/A';
                },
              },
              ...(data.type === 'add_to_cart' ? [
                {
                  key: 'totalQuantity',
                  label: 'Quantity',
                  width: 100,
                  render: (value) => value || 0,
                },
                {
                  key: 'productType',
                  label: 'Type',
                  width: 80,
                  render: (value) => (
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '600',
                      backgroundColor: value === 'DS' ? '#dbeafe' : '#fef3c7',
                      color: value === 'DS' ? '#1e40af' : '#92400e',
                    }}>
                      {value || 'N/A'}
                    </span>
                  ),
                },
              ] : []),
              {
                key: 'count',
                label: data.type === 'pdp_open' ? 'Opens' : 'Adds',
                width: 100,
              },
              {
                key: 'lastTimestamp',
                label: 'Last Activity',
                width: 180,
                render: (value) => value ? formatDate(new Date(value)) : 'N/A',
              },
            ]}
            userId={currentUserId}
            showToolbar={true}
            sortable={true}
            resizable={true}
            toolbarRightActions={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '13px', color: '#64748b' }}>Rows:</label>
                <select
                  value={detailsRowsPerPage}
                  onChange={(e) => {
                    const nextLimit = Number(e.target.value);
                    setDetailsRowsPerPage(nextLimit);
                    setDetailsCurrentPage(1);
                    updateDetailQueryPageLimit(1, nextLimit);
                  }}
                  style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', backgroundColor: '#fff', fontSize: '13px', cursor: 'pointer' }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            }
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '6px', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '13px', color: '#64748b' }}>
              Showing {productPagination.startIndex} to {productPagination.endIndex} of {productPagination.totalCount}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => {
                  const prevPage = productPagination.currentPage - 1;
                  setDetailsCurrentPage(prevPage);
                  updateDetailQueryPageLimit(prevPage, detailsRowsPerPage);
                }}
                disabled={!productPagination.hasPrevPage || loading}
                style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', backgroundColor: (!productPagination.hasPrevPage || loading) ? '#f1f5f9' : '#fff', color: (!productPagination.hasPrevPage || loading) ? '#94a3b8' : '#475569', cursor: (!productPagination.hasPrevPage || loading) ? 'not-allowed' : 'pointer', fontSize: '13px' }}
              >
                Previous
              </button>
              <span style={{ fontSize: '13px', color: '#64748b' }}>
                Page {productPagination.currentPage} of {productPagination.totalPages}
              </span>
              <button
                onClick={() => {
                  const nextPage = productPagination.currentPage + 1;
                  setDetailsCurrentPage(nextPage);
                  updateDetailQueryPageLimit(nextPage, detailsRowsPerPage);
                }}
                disabled={!productPagination.hasNextPage || loading}
                style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', backgroundColor: (!productPagination.hasNextPage || loading) ? '#f1f5f9' : '#fff', color: (!productPagination.hasNextPage || loading) ? '#94a3b8' : '#475569', cursor: (!productPagination.hasNextPage || loading) ? 'not-allowed' : 'pointer', fontSize: '13px' }}
              >
                Next
              </button>
            </div>
          </div>
        </>
      );
    } else if (data.type === 'image-search') {
      const imageSearchRows = (data.products || []).map((item, index) => ({
        sno: ((imageProductsCurrentPage - 1) * imageProductsRowsPerPage) + index + 1,
        ...item,
      }));

      // Extract common keywords from product titles
      const extractCommonKeywords = () => {
        const products = data.products || [];
        if (products.length === 0) return [];

        // Stop words to ignore
        const stopWords = new Set([
          'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from', 'has', 'he',
          'in', 'is', 'it', 'its', 'of', 'on', 'that', 'the', 'to', 'was', 'will', 'with',
          'the', 'this', 'but', 'they', 'have', 'had', 'what', 'when', 'where', 'who', 'which',
          'their', 'said', 'each', 'she', 'do', 'how', 'if', 'or', 'so', 'up', 'out', 'no', 'we',
          '&', '-'
        ]);

        const wordCount = {};

        // Extract words from all titles
        products.forEach(product => {
          if (!product.title) return;
          
          // Split by spaces, commas, and special characters
          const words = product.title
            .toLowerCase()
            .replace(/[^\w\s]/g, ' ') // Replace special chars with space
            .split(/\s+/)
            .filter(word => 
              word.length > 2 && // At least 3 characters
              !stopWords.has(word) &&
              !/^\d+$/.test(word) // Not purely numeric
            );

          words.forEach(word => {
            wordCount[word] = (wordCount[word] || 0) + 1;
          });
        });

        // Filter words appearing more than once and sort by frequency
        const commonWords = Object.entries(wordCount)
          .filter(([_, count]) => count > 1)
          .sort((a, b) => b[1] - a[1]) // Sort by count descending
          .map(([word, count]) => ({ word, count }));

        return commonWords;
      };

      const commonKeywords = extractCommonKeywords();
      
      // Collect all keywords already assigned to any product in this image
      const allAssignedKeywords = new Set();
      (data.products || []).forEach(product => {
        const keywordArray = Array.isArray(product.keywordSearch) ? product.keywordSearch : [];
        keywordArray.forEach(kw => allAssignedKeywords.add(kw));
      });

      return (
        <>
          {/* Common Keywords Section */}
          {commonKeywords.length > 0 && (
            <div style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              padding: '12px 16px',
              marginBottom: '16px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h4 style={{
                  margin: 0,
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#475569'
                }}>
                  Common Keywords ({commonKeywords.length})
                  {selectedKeywords.length > 0 && (
                    <span style={{ marginLeft: '8px', color: '#3b82f6', fontSize: '12px' }}>
                      ({selectedKeywords.length} selected)
                    </span>
                  )}
                </h4>
                {selectedKeywords.length > 0 && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={async () => {
                        setAssigningKeywords(true);
                        try {
                          const response = await SearchLogService.assignKeywordsToProducts(data.imageId, selectedKeywords);
                          if (response?.result?.code === 200 || response?.status?.code === 200) {
                            const responseData = response?.result?.data || response?.data || {};
                            const modifiedCount = responseData?.modifiedCount || 0;
                            message.showToast(`Successfully assigned ${selectedKeywords.length} keyword(s) to ${modifiedCount} products`, ToastTypes.SUCCESS);
                            setSelectedKeywords([]);
                            if (data.imageId) {
                              await fetchImageProductsByPage(data.imageId, imageProductsCurrentPage, imageProductsRowsPerPage);
                            }
                          } else {
                            message.showToast('Failed to assign keywords', ToastTypes.ERROR);
                          }
                        } catch (error) {
                          console.error('Error assigning keywords:', error);
                          message.showToast('Error assigning keywords', ToastTypes.ERROR);
                        } finally {
                          setAssigningKeywords(false);
                        }
                      }}
                      disabled={assigningKeywords}
                      style={{
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        padding: '6px 16px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: assigningKeywords ? 'not-allowed' : 'pointer',
                        opacity: assigningKeywords ? 0.6 : 1
                      }}
                    >
                      {assigningKeywords ? 'Assigning...' : `Assign to ${data.pagination?.totalCount || (data.products || []).length} Products`}
                    </button>
                    <button
                      onClick={() => setSelectedKeywords([])}
                      disabled={assigningKeywords}
                      style={{
                        background: '#f3f4f6',
                        color: '#475569',
                        border: '1px solid #e2e8f0',
                        padding: '6px 16px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: assigningKeywords ? 'not-allowed' : 'pointer',
                        opacity: assigningKeywords ? 0.6 : 1
                      }}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px',
              }}>
                {commonKeywords.slice(0, 30).map((item, idx) => {
                  const isSelected = selectedKeywords.includes(item.word);
                  const isAlreadyAssigned = allAssignedKeywords.has(item.word);
                  const isDisabled = isAlreadyAssigned;
                  
                  return (
                    <span
                      key={idx}
                      onClick={() => {
                        if (!isDisabled) {
                          if (isSelected) {
                            setSelectedKeywords(prev => prev.filter(w => w !== item.word));
                          } else {
                            setSelectedKeywords(prev => [...prev, item.word]);
                          }
                        }
                      }}
                      style={{
                        background: isAlreadyAssigned ? '#dcfce7' : (isSelected ? '#3b82f6' : '#f1f5f9'),
                        padding: '4px 10px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: isAlreadyAssigned ? '#166534' : (isSelected ? '#ffffff' : '#334155'),
                        border: isAlreadyAssigned ? '1px solid #86efac' : (isSelected ? '1px solid #2563eb' : '1px solid #e2e8f0'),
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        userSelect: 'none',
                        transition: 'all 0.2s',
                        opacity: isDisabled ? 0.7 : 1
                      }}
                      onMouseEnter={(e) => {
                        if (!isDisabled && !isSelected) {
                          e.currentTarget.style.background = '#e2e8f0';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isDisabled && !isSelected) {
                          e.currentTarget.style.background = '#f1f5f9';
                        }
                      }}
                    >
                      {item.word} <strong>({item.count})</strong>
                      {isAlreadyAssigned && <span style={{ marginLeft: '4px' }}>✓</span>}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Product Table */}
          <ExcelTable
            title={`Products for Image ID: ${data.imageId || 'N/A'} (Total: ${data.pagination?.totalCount || (data.products || []).length})`}
            data={imageSearchRows}
            columns={[
              {
                key: 'sno',
                label: 'SNO',
                width: 60,
              },
              {
                key: 'imgUrl',
                label: 'Image',
                width: 100,
                render: (value, row) => {
                  // Prioritize thumbnailImage over imgUrl
                  let imageUrl = row.thumbnailImage || value || row.imgUrl || row.imageUrl;
                  
                  // If it's an array, get the first valid URL
                  if (Array.isArray(imageUrl)) {
                    imageUrl = imageUrl.find(url => url && url.trim() !== '') || imageUrl[0];
                  }
                  
                  return (
                    <>
                      {imageUrl && (
                        <img 
                          src={imageUrl} 
                          alt="Product" 
                          style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      )}
                      <div style={{ width: '60px', height: '60px', backgroundColor: '#f3f4f6', borderRadius: '4px', display: imageUrl ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#9ca3af', textAlign: 'center', padding: '4px' }}>
                        {imageUrl ? 'Failed' : 'No Image'}
                      </div>
                    </>
                  );
                },
              },
              {
                key: 'sku',
                label: 'SKU',
                width: 150,
              },
              {
                key: 'title',
                label: 'Product Title',
                width: 350,
                wrap: true,
              },
            ]}
            userId={currentUserId}
            showToolbar={true}
            sortable={true}
            resizable={true}
            toolbarRightActions={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '13px', color: '#64748b' }}>Rows:</label>
                <select
                  value={imageProductsRowsPerPage}
                  onChange={(e) => {
                    const nextLimit = Number(e.target.value);
                    setImageProductsRowsPerPage(nextLimit);
                    setImageProductsCurrentPage(1);
                    if (data.imageId) {
                      const nextQuery = new URLSearchParams(window.location.search);
                      nextQuery.set('type', 'image-search');
                      nextQuery.set('imageId', data.imageId);
                      nextQuery.set('page', '1');
                      nextQuery.set('limit', String(nextLimit));
                      window.history.replaceState({}, '', `${window.location.pathname}?${nextQuery.toString()}`);
                      fetchImageProductsByPage(data.imageId, 1, nextLimit);
                    }
                  }}
                  style={{
                    padding: '4px 8px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            }
          />

          {data.pagination && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '12px',
                padding: '12px',
                backgroundColor: '#f8fafc',
                borderRadius: '6px',
                gap: '12px',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ fontSize: '13px', color: '#64748b' }}>
                Showing {data.pagination.totalCount === 0 ? 0 : ((data.pagination.currentPage - 1) * data.pagination.limit) + 1} to {Math.min(data.pagination.currentPage * data.pagination.limit, data.pagination.totalCount)} of {data.pagination.totalCount}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => {
                    if (data.imageId && data.pagination.currentPage > 1) {
                      const prevPage = data.pagination.currentPage - 1;
                      const nextQuery = new URLSearchParams(window.location.search);
                      nextQuery.set('type', 'image-search');
                      nextQuery.set('imageId', data.imageId);
                      nextQuery.set('page', String(prevPage));
                      nextQuery.set('limit', String(imageProductsRowsPerPage));
                      window.history.replaceState({}, '', `${window.location.pathname}?${nextQuery.toString()}`);
                      fetchImageProductsByPage(data.imageId, prevPage, imageProductsRowsPerPage);
                    }
                  }}
                  disabled={!data.pagination.hasPrevPage || loading}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '4px',
                    backgroundColor: (!data.pagination.hasPrevPage || loading) ? '#f1f5f9' : '#fff',
                    color: (!data.pagination.hasPrevPage || loading) ? '#94a3b8' : '#475569',
                    cursor: (!data.pagination.hasPrevPage || loading) ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                  }}
                >
                  Previous
                </button>
                <span style={{ fontSize: '13px', color: '#64748b' }}>
                  Page {data.pagination.currentPage} of {data.pagination.totalPages || 1}
                </span>
                <button
                  onClick={() => {
                    if (data.imageId && data.pagination.hasNextPage) {
                      const nextPage = data.pagination.currentPage + 1;
                      const nextQuery = new URLSearchParams(window.location.search);
                      nextQuery.set('type', 'image-search');
                      nextQuery.set('imageId', data.imageId);
                      nextQuery.set('page', String(nextPage));
                      nextQuery.set('limit', String(imageProductsRowsPerPage));
                      window.history.replaceState({}, '', `${window.location.pathname}?${nextQuery.toString()}`);
                      fetchImageProductsByPage(data.imageId, nextPage, imageProductsRowsPerPage);
                    }
                  }}
                  disabled={!data.pagination.hasNextPage || loading}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '4px',
                    backgroundColor: (!data.pagination.hasNextPage || loading) ? '#f1f5f9' : '#fff',
                    color: (!data.pagination.hasNextPage || loading) ? '#94a3b8' : '#475569',
                    cursor: (!data.pagination.hasNextPage || loading) ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      );
    }
    return null;
  };

  const headerDetail = data.keyword || data.userEmail || data.imageId || 'Details';

  return (
    <div className="user-details-page" style={{ padding: '20px', background: '#f8fafc', minHeight: '100vh' }}>
      <div className="user-details-header" style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        padding: '16px 24px',
        background: 'white',
        borderRadius: '8px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <div className="header-left" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button 
            onClick={() => window.close()} 
            className="back-btn" 
            title="Close Tab"
            style={{
              background: '#f3f4f6',
              border: 'none',
              borderRadius: '6px',
              padding: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = '#e5e7eb'}
            onMouseLeave={(e) => e.target.style.background = '#f3f4f6'}
          >
            <FiX size={18} />
          </button>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#0f172a' }}>
            {data.title || 'User Details'} - {headerDetail}
          </h2>
        </div>
      </div>
      <div className="user-details-content" style={{ background: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
        {renderTable()}
      </div>
      {/* Hover Card - Rendered outside table to avoid overflow clipping */}
      {hoveredKeywordId && data?.type === 'searchKeywords' && (() => {
        const keyword = data.keywords?.find(k => `${k.keyword}-${k.ip || 'unknown'}` === hoveredKeywordId);
        const timestamps = keyword?.searchTimestamps || [];
        if (timestamps.length === 0) return null;
        return (
          <div 
            className="keyword-hover-card keyword-hover-card-fixed"
            style={{
              top: `${hoverCardPosition.top}px`,
              left: `${hoverCardPosition.left}px`,
              transform: 'translateY(calc(-100% - 8px))',
            }}
            onMouseEnter={() => setHoveredKeywordId(hoveredKeywordId)}
            onMouseLeave={() => setHoveredKeywordId(null)}
          >
            <div className="keyword-card-header">SEARCH TIMESTAMPS ({timestamps.length})</div>
            <div className="keyword-card-content">
              {timestamps.map((ts, tsIdx) => (
                <div key={tsIdx} style={{ padding: '4px 0', borderBottom: tsIdx < timestamps.length - 1 ? '1px solid #374151' : 'none' }}>
                  {formatDate(new Date(ts))}
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default UserDetails;

